import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { SCHOOL_DEPARTMENTS, SCHOOL_PERMISSIONS } from '@jupi/shared'
import { useAuth } from '@jupi/ui'
import { useLessonBriefings } from '../api/briefings'

// The briefing pack sections that cover a lesson (issue #268): "Open
// briefing" for anyone who can read the pack, "Present" for those who
// teach, which steps through the pack with the student and, given the
// enrolment, hands back to the flight log with the briefing marked done.
// The syllabus's own briefing link is the fallback when no pack covers the
// lesson.
export default function LessonBriefing({
  lessonId,
  fallbackUrl,
  enrolmentId,
}: {
  lessonId: number
  fallbackUrl?: string | null
  enrolmentId?: number
}) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { data: packs = [], isLoading } = useLessonBriefings(lessonId)
  const instructs = can(...SCHOOL_DEPARTMENTS.flatMap((d) => [SCHOOL_PERMISSIONS[d].instruct, SCHOOL_PERMISSIONS[d].manage]))

  if (isLoading) return null
  if (packs.length === 0) {
    return fallbackUrl ? (
      <Button size="small" href={fallbackUrl} target="_blank" rel="noreferrer" startIcon={<Icon icon="mdi:open-in-new" />}>
        {t('school.briefingMaterial')}
      </Button>
    ) : null
  }
  const present = new URLSearchParams({ present: '1', lesson: String(lessonId) })
  if (enrolmentId) present.set('logFor', String(enrolmentId))
  return (
    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {packs.map((p) => (
        <Stack key={p.packId} direction="row" spacing={1}>
          <Button
            size="small"
            component={RouterLink}
            to={`/school/briefings/${p.packId}#${p.anchor}`}
            startIcon={<Icon icon="mdi:book-open-variant" />}
          >
            {packs.length > 1 ? t('briefings.otherLanguage', { title: t('briefings.open'), language: t(`briefings.lang_${p.language}`) }) : t('briefings.open')}
          </Button>
          {instructs ? (
            <Button
              size="small"
              variant="outlined"
              component={RouterLink}
              to={`/school/briefings/${p.packId}?${present.toString()}#${p.anchor}`}
              startIcon={<Icon icon="mdi:presentation" />}
            >
              {t('briefings.presentLesson')}
            </Button>
          ) : null}
        </Stack>
      ))}
    </Stack>
  )
}
